import type { Quiz } from "./types";

/**
 * The quiz with the most direct line to the product.
 *
 * Someone asking how they should pray is someone who has already decided to,
 * and has run into the fact that the method they were handed does not fit them.
 * Five honest answers to that, each ending in something to do this week.
 */
export const article: Quiz = {
  slug: "prayer-style-quiz",
  kind: "reflection",
  title: "What is your prayer style? A ten-question test",
  h1: "What is your prayer style?",
  description:
    "Ten questions on how you actually pray rather than how you were taught to — and five patterns, each with the reason it dries up and what to do about it.",
  targets: [
    "prayer style quiz",
    "what is my prayer style",
    "how should i pray test",
    "spiritual temperament quiz",
  ],
  published: "2026-08-26",
  standfirst:
    "Most people are handed one method, find it does not fit, and conclude they are bad at praying. Usually they were given somebody else's pattern.",
  takeaways: [
    "Five patterns: the listener, the walker, the writer, the intercessor and the reader — most people are a strong one and a weak second.",
    "The commonest cause of a dry prayer life is not a lack of discipline but a mismatched method, usually one inherited from whoever taught you.",
    "Each pattern has a characteristic failure: the listener stops when it feels like nothing, the walker never sits still, the writer performs, the intercessor turns prayer into administration, the reader recites.",
    "None of this is a personality type in any technical sense — it is a description of how you already pray when nobody has told you how.",
  ],
  intro: [
    {
      type: "p",
      text: "Almost everyone is taught one way to pray, usually by whoever was nearest when they started. Quiet time at a desk with a notebook. Out loud with other people. A list of names. A written prayer, said carefully. Each of those is a genuine tradition with centuries behind it, and each of them fits some people and not others — and the people it does not fit rarely conclude that the method is a poor match. They conclude that they are bad at praying, and they slowly stop.",
    },
    {
      type: "p",
      text: "So this asks what you already do when nobody has given you an instruction: where you are when a prayer actually happens, what makes you stop, and what you do with the ones that are not answered. Ten questions, and the result names both the pattern and the specific way it dries up.",
    },
    {
      type: "note",
      text: "This is a description, not a category. Most people come out strongly one thing and quietly a second, and the second is usually the one worth developing.",
    },
  ],
  questions: [
    {
      id: "where",
      prompt: "Where does a real prayer most often happen for you?",
      options: [
        {
          text: "Sitting still, somewhere quiet, eyes shut.",
          scores: { listener: 3 },
        },
        {
          text: "Walking, driving, washing up — moving.",
          scores: { walker: 3 },
        },
        { text: "At a page, with a pen.", scores: { writer: 3 } },
        {
          text: "In church, or with other people, out loud.",
          scores: { reader: 2, intercessor: 1 },
        },
      ],
    },
    {
      id: "start",
      prompt: "How does it usually start?",
      options: [
        {
          text: "With silence, for a while, before anything is said.",
          scores: { listener: 3 },
        },
        {
          text: "With whatever is on top — I talk first and sort it out later.",
          scores: { walker: 2, writer: 1 },
        },
        {
          text: "With a name. Somebody I said I would pray for.",
          scores: { intercessor: 3 },
        },
        {
          text: "With words somebody else wrote — a psalm, a collect, the Lord's Prayer.",
          scores: { reader: 3 },
        },
      ],
    },
    {
      id: "stops",
      prompt: "What most reliably ends it early?",
      options: [
        {
          text: "The feeling that nothing is happening.",
          scores: { listener: 3 },
        },
        {
          text: "I arrive somewhere, or the task finishes.",
          scores: { walker: 3 },
        },
        {
          text: "I start editing the sentence instead of meaning it.",
          scores: { writer: 3 },
        },
        {
          text: "The list is done and I have not stopped to think about any of it.",
          scores: { intercessor: 3 },
        },
      ],
    },
    {
      id: "unanswered",
      prompt: "Something you have prayed about for years has not changed.",
      options: [
        {
          text: "I keep sitting with it. The sitting is the prayer.",
          scores: { listener: 3 },
        },
        {
          text: "I have stopped naming it and it comes up on its own, often.",
          scores: { walker: 2, listener: 1 },
        },
        {
          text: "It is written down, with dates, and I go back and read them.",
          scores: { writer: 3, intercessor: 1 },
        },
        {
          text: "It is still on the list. It goes on the list every time.",
          scores: { intercessor: 3 },
        },
      ],
    },
    {
      id: "words",
      prompt: "Written prayers — a psalm, a liturgy, a book of collects.",
      options: [
        {
          text: "They carry me when I have nothing of my own.",
          scores: { reader: 3 },
        },
        {
          text: "I use one to start and then leave it behind.",
          scores: { reader: 1, listener: 1, walker: 1 },
        },
        { text: "I would rather write my own.", scores: { writer: 3 } },
        {
          text: "They feel like somebody else's words in my mouth.",
          scores: { walker: 2, intercessor: 1 },
        },
      ],
    },
    {
      id: "outloud",
      prompt: "Praying out loud in front of other people.",
      options: [
        {
          text: "Fine, and it is often when I mean it most.",
          scores: { intercessor: 2, reader: 1 },
        },
        {
          text: "I do it, and I hear myself performing.",
          scores: { writer: 2, listener: 1 },
        },
        {
          text: "I would rather not, and I do not think it is shyness.",
          scores: { listener: 2, walker: 1 },
        },
        {
          text: "Better than alone, honestly. Alone I drift.",
          scores: { reader: 2, intercessor: 1 },
        },
      ],
    },
    {
      id: "bible",
      prompt: "Reading scripture and praying — how do they sit together?",
      options: [
        {
          text: "The reading is where the praying comes from. One passage, slowly.",
          scores: { reader: 3, listener: 1 },
        },
        {
          text: "I read, then I write about it, then that becomes the prayer.",
          scores: { writer: 3 },
        },
        {
          text: "They are separate. The prayer happens elsewhere, usually moving.",
          scores: { walker: 3 },
        },
        {
          text: "I read a bit and then I am off praying for people it reminded me of.",
          scores: { intercessor: 3 },
        },
      ],
    },
    {
      id: "notice",
      prompt: "Which is closest to how you notice God during an ordinary week?",
      options: [
        { text: "In the quiet, when I finally stop.", scores: { listener: 3 } },
        {
          text: "Outdoors, or in motion, mostly without meaning to.",
          scores: { walker: 3 },
        },
        {
          text: "Afterwards, when I write down what happened.",
          scores: { writer: 3 },
        },
        {
          text: "Through other people — what they say, what they need.",
          scores: { intercessor: 3 },
        },
      ],
    },
    {
      id: "hard",
      prompt: "On a genuinely hard day, what do you reach for?",
      options: [
        {
          text: "Silence, and staying there longer than is comfortable.",
          scores: { listener: 3 },
        },
        { text: "A long walk with nobody else.", scores: { walker: 3 } },
        {
          text: "A page. It does not come out straight until it is written.",
          scores: { writer: 3 },
        },
        {
          text: "A psalm. Somebody has already said it better.",
          scores: { reader: 3 },
        },
      ],
    },
    {
      id: "phone",
      prompt: "Where is your phone during all of this?",
      options: [
        {
          text: "In another room, deliberately.",
          scores: { listener: 2, walker: 1 },
        },
        {
          text: "In my hand — it is the Bible, the notes and the list.",
          scores: { intercessor: 2, writer: 1 },
        },
        {
          text: "In my pocket, and I check it more than I would admit.",
          scores: { walker: 2, reader: 1 },
        },
        {
          text: "It is the reason this is shorter than it used to be.",
          scores: { intercessor: 1, writer: 1, walker: 1 },
        },
      ],
    },
  ],
  outcomes: [
    {
      id: "listener",
      name: "The listener",
      summary:
        "You pray by stopping. The silence is not the preparation for prayer, it is the prayer — which is why it collapses the moment you start measuring it.",
      body: [
        "Your answers describe someone who arrives at prayer by subtraction. Fewer words, a shut door, a long pause before anything is said. This is the contemplative end of a very old tradition and it is genuinely uncomfortable at first, which is why most people who are built for it think they are failing at it.",
        "The characteristic failure is the one you named: you stop when it feels like nothing is happening. That is worth taking seriously and then not obeying, because the feeling is not the instrument. Almost every writer in this tradition, over fifteen centuries, describes long stretches where nothing is felt, and treats them as ordinary weather rather than as evidence.",
        "The second risk is subtler. Listening can drift into a private practice that never touches anything — no names, no obligations, nobody else's problems in it. The correction is not less silence. It is one fixed commitment to somebody outside the room.",
      ],
      verse: {
        text: "Be still, and know that I am God.",
        ref: "Psalm 46:10",
      },
      practice: [
        "Set a timer for twelve minutes and stay for the whole of it, including the part where it feels pointless.",
        "Take one sentence of scripture in with you rather than an empty head — a phrase, not a chapter.",
        "Put one name in at the end, every time. It keeps the practice from closing in on itself.",
      ],
    },
    {
      id: "walker",
      name: "The walker",
      summary:
        "You pray in motion. Sitting still is where your prayer goes to be interrupted, and the best conversations you have had were on foot.",
      body: [
        "The pattern in your answers is that prayer happens while something else is happening — the walk, the drive, the sink full of dishes. This is not a lesser form of it. A great deal of the praying in scripture happens on roads, and the desk-and-notebook model is a fairly recent and fairly specific cultural default that has been handed to everybody as if it were the only shape available.",
        "The failure mode is that it never gets protected. Because it happens alongside something else, it is never scheduled, and a thing that is never scheduled quietly disappears in a busy season — and you will notice it as a vague spiritual flatness rather than as a missing appointment, which makes it hard to diagnose.",
        "The other cost is that undirected prayer stays shallow. Moving prayer wanders by design, and wandering is good for a while and then becomes a way of never staying with anything difficult for longer than four minutes.",
      ],
      verse: {
        text: "Pray without ceasing.",
        ref: "1 Thessalonians 5:17",
      },
      practice: [
        "Give one specific walk a week to prayer and put it in the calendar like an appointment, because otherwise it is not one.",
        "Take one subject with you rather than none — the same one, for a month.",
        "Leave the headphones at home for that walk. Input is the thing that quietly replaced it.",
      ],
    },
    {
      id: "writer",
      name: "The writer",
      summary:
        "You find out what you think by writing it down, and that includes prayer. The page is not a record of it; it is where it happens.",
      body: [
        "Your answers point at someone whose thinking is externalised. Spoken prayer comes out approximate and half-meant; written prayer comes out true, because the pen is slow enough to catch you. The psalms are the obvious precedent — a hundred and fifty prayers that exist because somebody wrote them down, complaints included.",
        "The failure you named yourself: you start editing. The sentence stops being addressed to God and starts being a sentence, and once that switch flips the rest of the page is craft rather than prayer. It is a genuinely difficult problem, and the practical answer is ugly handwriting, no rereading, and a cheap notebook nobody would ever publish from.",
        "The advantage nobody mentions is that this is the only pattern with a memory. A written prayer life can be read back, and reading back two years of it is the single most convincing evidence most people ever get that anything was happening.",
      ],
      verse: {
        text: "I pour out my complaint before him. I tell him my troubles.",
        ref: "Psalm 142:2",
      },
      practice: [
        "Write for ten minutes without stopping and without going back to fix a sentence. Legibility is not the goal.",
        "Date everything, and reread the same month a year later. That is where the pattern shows up.",
        "Keep one page for things asked and one for things noticed. The second page fills up more slowly and matters more.",
      ],
    },
    {
      id: "intercessor",
      name: "The intercessor",
      summary:
        "You pray for people, by name, and you remember. It is the most useful of these patterns and the easiest to turn into administration.",
      body: [
        "Your answers describe prayer that begins with somebody else. A name, a situation, a promise you made in a corridor and kept. Churches run on people who do this, and most of them have no idea how much of the load they are carrying, because it is invisible by definition.",
        "The failure is the one you named: the list gets completed rather than prayed. Twenty names in four minutes, each one a task cleared, and none of them held for long enough to feel anything. Once prayer becomes a queue, it produces the exact fatigue that any long queue produces, and the honest response — quietly dropping the list — feels like a spiritual failure rather than a workload problem.",
        "The other cost is that you may not appear on your own list. People whose prayer is entirely outward-facing are often unable to say what they need, sometimes for years. It reads as humility and it functions as avoidance, and the people around you generally notice it before you do.",
      ],
      verse: {
        text: "I exhort therefore, first of all, that petitions, prayers, intercessions, and givings of thanks be made for all men.",
        ref: "1 Timothy 2:1",
      },
      practice: [
        "Cut the daily list to five names and give each one a whole minute. Rotate the rest weekly.",
        "Put yourself on it once a week, with an actual request rather than a general one.",
        "Tell one person you prayed for them. It converts an invisible practice into a real one for both of you.",
      ],
    },
    {
      id: "reader",
      name: "The reader",
      summary:
        "You pray best with words that are already written — a psalm, a collect, the Lord's Prayer — and you are not borrowing. You are joining in.",
      body: [
        "Your answers show someone who is carried by given words. That instinct has an enormous amount of history behind it: the psalter has been the prayer book of the church for most of its existence, and the objection that written prayer is second-hand would have been unintelligible for the majority of that time. Given words hold you up on the days when you have nothing of your own, which is most of the days that matter.",
        "The failure is recitation. The words are good enough to carry you through while you are somewhere else entirely, and you can finish a psalm having genuinely thought about none of it. Fluency is the enemy here in the same way that fluency is the enemy in a well-remembered story.",
        "The correction is slowness and interruption: one psalm rather than a set, read aloud, stopped in the middle when a line snags. And occasionally, deliberately, saying something in your own words even when they come out badly — badly is the point.",
      ],
      verse: {
        text: "Lord, teach us to pray.",
        ref: "Luke 11:1",
      },
      practice: [
        "Read one psalm aloud, slowly, and stop at the first line that catches. Stay there rather than finishing.",
        "Pray one thing in your own words at the end of it, however clumsy.",
        "Learn one prayer by heart this month, so you have it on the days you cannot read anything.",
      ],
    },
  ],
  after: [
    {
      type: "h2",
      id: "why-mismatch",
      text: "Why a mismatched method feels like a personal failure",
    },
    {
      type: "p",
      text: "Because the instruction almost always arrives as a general rule rather than as one option. Get up early, sit at a desk, use a notebook, work through a plan. For a listener or a writer that is close enough to right. For a walker it is a description of the conditions under which they will fall asleep or check their phone, and the conclusion they draw is not that the desk is wrong — it is that they lack discipline. Years can go past like that.",
    },
    {
      type: "p",
      text: "The test above is not trying to give anybody permission to skip the difficult parts. Each of the five results names the thing that particular pattern avoids, because every one of them has one. The point is only that the difficulty should be the real difficulty of prayer rather than the artificial difficulty of somebody else's method.",
    },
    {
      type: "h2",
      id: "the-common-obstacle",
      text: "The obstacle all five have in common",
    },
    {
      type: "p",
      text: "Interruption. Every pattern above needs an unbroken stretch — twelve minutes of silence, a walk without headphones, ten minutes at a page, five names given a minute each, one psalm read slowly. None of them survives a phone that lights up twice in the middle, and none of them survives being attempted at eleven at night after two hours of scrolling that were not planned either.",
    },
    {
      type: "p",
      text: "That is the specific problem Bible Shield exists for: it closes the apps that take your evenings at times you choose, and when you open one it asks for a short prayer first — thirty seconds, written for the day you are actually having. If your result was the intercessor or the writer and your phone is where the list and the notes live, {the-phone-and-faith-test} is the more uncomfortable companion to this one.",
      links: { "the-phone-and-faith-test": "/quiz/phone-and-faith-test" },
    },
    {
      type: "note",
      text: "Most people are one pattern strongly and a second one quietly. The second is usually where the next year of growth is.",
    },
  ],
  faq: [
    {
      q: "Is this based on a recognised framework?",
      a: "Loosely, and it does not claim to be an instrument. Several traditions describe something similar — contemplative, active, written, intercessory and liturgical prayer are old categories, and modern writers on spiritual temperaments cover related ground. The five here start from the failure modes.",
    },
    {
      q: "Can I be more than one?",
      a: "Almost everybody is. The result names the heaviest pattern in your answers; read the one just below it too, because the second is usually the underdeveloped one and therefore the more useful thing to work on.",
    },
    {
      q: "Does the result change what I should be praying about?",
      a: "No. It describes the form, not the content. Every pattern here still has to deal with confession, thanks, and the things that do not get answered — the difference is only in what shape that takes for you.",
    },
    {
      q: "What if none of them fits?",
      a: "Then the honest answer is that you have not found it yet, and the five above are not a complete map. Praying with one other person regularly is the option none of these covers, and for a substantial number of people it is the one that works.",
    },
  ],
  related: [
    "morning-quiet-time-routine",
    "digital-sabbath",
    "phone-addiction-bible-verses",
  ],
  relatedQuizzes: ["phone-and-faith-test", "which-bible-character-are-you"],
};
